import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-crimson/60 focus:ring-2 focus:ring-crimson/15 transition-all duration-300";

export const getToken = () => localStorage.getItem("vertex_admin_token");

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(", ");
  if (typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (getToken()) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("vertex_admin_token", data.access_token);
      toast.success("Welcome back, producer.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-gray-900 flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(50%_40%_at_50%_10%,rgba(255,42,59,0.07),transparent_60%)] pointer-events-none" />
      <div className="relative w-full max-w-md">
        <button
          type="button"
          data-testid="admin-back-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs text-gray-400 hover:text-crimson transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </button>
        <div className="rounded-3xl border border-gray-100 bg-white shadow-soft p-8 md:p-10">
          <LogoMark className="w-12 h-12" />
          <h1 className="font-display font-extrabold text-2xl mt-5 tracking-tight text-gray-900">Studio Login</h1>
          <p className="text-gray-500 text-sm mt-2">Internal access — review client briefs and submissions.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Email</label>
              <input
                data-testid="admin-input-email"
                type="email"
                className={fieldClass}
                placeholder="admin@vertexproduction.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Password</label>
              <input
                data-testid="admin-input-password"
                type="password"
                className={fieldClass}
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="btn-glow w-full py-3.5 rounded-xl bg-crimson text-white font-bold text-sm tracking-wide disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
